import React, { useMemo, useState, useEffect, useCallback, Fragment, useRef } from 'react';
import OrderPallets from './OrderPallets';
import { connect } from 'react-redux';
import { updateOrder } from '../../websocket';

function getColli(abc) {
	return abc.slice().sort((lhs, rhs) => {
		if(lhs._id < rhs._id) {
			return -1;
		} else if(lhs._id > rhs._id) {
			return 1;
		}
		return 0;
	}).reduce((all, o) => all.concat(o.pallets), []);
}

function deepEquals(a, b) {
	if(a === b) {
		return true;
	} else if(typeof a === 'object' && typeof b === 'object') {
		if(Array.isArray(a)) {
			if(!Array.isArray(b) || a.length !== b.length) {
				return false;
			}
			return a.every((v, i) => deepEquals(v, b[i]));
		}
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if(aKeys.length !== bKeys.length) {
			return false;
		}
		return aKeys.every(key => key in b && deepEquals(a[key], b[key]));
	}
	return false;
}

function samePallet(p1, p2) {
	if(p1 === p2) {
		return true;
	} else if(p1.pallet === p2.pallet) {
		return ['length', 'width', 'height', 'weight', 'warehouse', 'packingAids'].every(key => {
			if(p1[key] || p2[key]) {
				return deepEquals(p1[key], p2[key]);
			}
			return true;
		});
	}
	return false;
}

function samePallets(c1, c2) {
	if(c1.length !== c2.length) {
		return false;
	}
	for(let i = 0; i < c1.length; i++) {
		if(!samePallet(c1[i], c2[i])) {
			return false;
		}
	}
	return true;
}

function hasBeenPrinted(abc) {
	return abc.some(order => {
		if(order.lastPrinted) {
			return !order.lastPalletUpdate || order.lastPrinted > order.lastPalletUpdate;
		}
		return false;
	});
}

function filterColli(colli, pallets) {
	if(!pallets.length || !colli.length) {
		return colli;
	}
	return colli.filter(c => {
		const pallet = pallets.find(p => p.id === c.pallet);
		if(!pallet || pallet.type === 'DIMENSIONLESS') {
			return true;
		} else if(!c.height) {
			return false;
		} else if(pallet.type === 'BOX') {
			return c.width && c.length && c.weight;
		}
		return true;
	});
}

function PalletContainer({ pallets, readonly, abc, children, order, warehouse, locations }) {
	const ref = useRef({});
	const [colli, setColli] = useState(() => getColli(abc));
	const [newStickersPrompt, setNewStickersPrompt] = useState(() => hasBeenPrinted(abc));
	useEffect(() => {
		if(!ref.current.orders) {
			ref.current.orders = abc.map(o => o._id);
		} else if(!ref.current.changed || ref.current.orders.some(id => !abc.some(o => o._id === id))) {
			setColli(getColli(abc));
		}
		setNewStickersPrompt(hasBeenPrinted(abc));
	}, abc);
	useEffect(() => {
		const toSave = filterColli(colli, pallets);
		const current = getColli(abc);
		if(!samePallets(toSave, current) && !samePallets(colli, current)) {
			const { _id } = order;
			updateOrder(_id, 'pallets', toSave);
			abc.forEach(o => {
				if(o._id !== _id) {
					updateOrder(o._id, 'pallets', []);
				}
			});
		}
	}, [colli, pallets]);
	const byPallet = useMemo(() => {
		const out = {};
		colli.forEach((c, i) => {
			if(!out[c.pallet]) {
				out[c.pallet] = [];
			}
			out[c.pallet].push({ colli: c, i });
		});
		return out;
	}, [colli]);
	const setPallet = useCallback((type, quantity) => {
		if(isNaN(quantity)) {
			return;
		}
		ref.current.changed = true;
		setColli(c => {
			let found = c.filter(p => p.pallet === type);
			if(found.length < quantity) {
				const out = c.slice();
				for(let i = 0; i < quantity - found.length; i++) {
					out.push({ pallet: type, warehouse });
				}
				return out;
			}
			found = found.slice(quantity);
			return c.filter(p => !found.includes(p));
		});
	}, [setColli]);
	const filtered = useMemo(() => pallets.filter(p => p.dropship === order.dropship), [pallets]);
	const warehouses = useMemo(() => {
		const out = {};
		Object.values(locations).forEach(key => {
			out[key] = true;
		});
		return Object.keys(out).map(label => ({ value: label, label }));
	}, [locations]);
	return <Fragment>
		<OrderPallets pallets={filtered} readonly={readonly} abc={abc} colli={colli} setPallet={setPallet} setColli={setColli} dropship={order.dropship} byPallet={byPallet} newStickersPrompt={newStickersPrompt} setNewStickersPrompt={setNewStickersPrompt} warehouses={warehouses} />
		{children}
		<OrderPallets pallets={filtered} readonly={readonly} abc={abc} colli={colli} setPallet={setPallet} setColli={setColli} dropship={order.dropship} byPallet={byPallet} newStickersPrompt={newStickersPrompt} setNewStickersPrompt={setNewStickersPrompt} warehouses={warehouses} />
	</Fragment>;
}

const PalletWrapper = connect(({ pallets, warehouse, locations }) => {
	return { pallets, warehouse, locations };
})(PalletContainer);

export default PalletWrapper;
